<template>
  <master-layout pageTitle="Whatsapp Widget">
    <div>
      <div class="toolbar-header">
        <VIcon @click="() => $router.go(-1)" :size="-4" name="arrow-left-line" class="cursorPointer" />
        <h2 class="vtext-card-title--light">WhatsApp widget</h2>
      </div>

      <!-- Mobile tab switcher -->
      <div class="mobile-tabs">
        <button :class="['tab-item', { active: activeTab === 'widget' }]" @click="activeTab = 'widget'">Whatsapp widget</button>
        <button :class="['tab-item', { active: activeTab === 'preview' }]" @click="activeTab = 'preview'">Preview</button>
      </div>

      <div class="container">
        <div class="vrow">
          <div class="vcol vcol--no-padding vcol-xl-8 vcol-lg-8 vcol-md-8 vcol-sm-12 vcol-xs-12">
            <div v-if="activeTab === 'widget'" class="model-sections customMarginBottom">
              <div class="custBorder sectionPadding">
                <h3 class="vtext-card-title--light vpb-10">Generate embed code</h3>
                <p class="vtext-subtitle">Customize your WhatsApp widget and embed it on your webview!</p>
              </div>
              <div class="sectionPadding">
                <div class="options-row custBorder">
                  <p class="vtext-subtitle">Background color</p>
                  <div class="flexContainer">
                    <span class="vtext-body paddingRight">{{ welcomeMessage.theme_color }}</span>
                    <div class="colorPickerWrapper" :style="{'background-color': welcomeMessage.theme_color}">
                      <input v-model="welcomeMessage.theme_color" type="color" class="themePicker" aria-label="Background color" />
                    </div>
                  </div>
                </div>

                <div class="options-row custBorder">
                  <p class="vtext-subtitle">Text color</p>
                  <div class="flexContainer">
                    <span class="vtext-body paddingRight">{{ welcomeMessage.text_color }}</span>
                    <div class="colorPickerWrapper" :style="{'background-color': welcomeMessage.text_color}">
                      <input v-model="welcomeMessage.text_color" type="color" class="themePicker" aria-label="Text color" />
                    </div>
                  </div>
                </div>

                <div class="customMarginBottom">
                  <p class="vtext-subtitle vpb-10">Greeting text (Max 40 characters)</p>
                  <div class="counter-wrapper">
                    <VTextField v-model="welcomeMessage.welcome_screen_greeting" :maxlength="40" placeholder="Enter greeting text" aria-label="greeting text" appearance="ghost" class="vivid-input"></VTextField>
                    <div class="custom-counter">
                      {{ (welcomeMessage.welcome_screen_greeting || '').length }}/40
                    </div>
                  </div>
                </div>

                <div>
                  <div class="intentContainer vpb-10">
                    <p class="vtext-subtitle">Intent message (Max 100 characters)</p>
                    <HelpTooltip :iconStyle="{'padding-top': '7px'}">Leverage this field to start a chatbot, human agent, sales and a whole lot more.</HelpTooltip>
                  </div>
                  <div class="counter-wrapper">
                    <VTextArea v-model="welcomeMessage.intent_message" :maxlength="100" placeholder="Enter a message" aria-label="Intent message" appearance="ghost" class="vivid-textarea"></VTextArea>
                    <div class="custom-counter">
                      {{ (welcomeMessage.intent_message || '').length }}/100
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="model-sections">
              <div class="sourceCodeHeader custBorder sectionPadding">
                <div>
                  <p class="vtext-card-title vpb-10">Source code</p>
                  <p class="vtext-subtitle">Copy and paste the below code inside the &lt;/body&gt; tag</p>
                </div>
                <VButton class="vvd-btn-primary" connotation="cta" appearance="filled" label="Copy to clipboard" icon="copy-2-line" @click="copy(widgetScript)"></VButton>
              </div>
              <div class="sectionPadding">
                <div class="code-block">
                <pre id="codeSnippet"><code>&lt;!-- WhatsApp Widget - by Jumper.ai --&gt;
                  &lt;style type="text/css"&gt;
                  .jmpr_whatsapp_roundButton:hover .jmpr_whatsapp_greeting_title_tooltip{opacity: 1 !important;}
                  .jmpr_whatsapp_roundButton .jmpr_whatsapp_greeting_title_tooltip::after{content: " "; position: absolute; top: 50%; left: 100%; margin-top: -5px; border-width: 5px; border-style: solid; border-color: transparent transparent transparent {{ welcomeMessage.theme_color }};}
                  &lt;/style&gt;

                  &lt;div id="jmpr-whatsapp-widget-container"
                    class="jmpr-whatsapp-widget-container"
                    data-whatsapp-number="{{ countryCodeWithout_plusSign(whatsAppSocialData?.country_code)}}{{whatsAppSocialData?.mobile_number}}"
                    data-whatsapp-intent-message="{{welcomeMessage.intent_message}}"
                    data-whatsapp-background-color="{{ welcomeMessage.theme_color }}"
                    data-whatsapp-text-color="{{ welcomeMessage.text_color }}"
                    data-whatsapp-greeting-title="{{ welcomeMessage.welcome_screen_greeting }}"
                    data-region="{{ isEuServer() ? 'eu' : 'us' }}"
                    style="position: fixed; z-index: 1000000; -webkit-backface-visibility: hidden; backface-visibility: hidden; bottom: 20px; right: 20px; display: inline-block; line-height: 1;"&gt;
                  &lt;/div&gt;

                  &lt;script src="{{ baseURL }}/assets/js/whatsapp-widget.js"&gt;&lt;/script&gt;
                  &lt;!-- WhatsApp Widget - by Jumper.ai --&gt;</code></pre>
                </div>
              </div>
            </div>
          </div>

          <!-- Preview -->
          <div class="vcol vcol--no-padding vcol-xl-4 vcol-lg-4 vcol-md-4 vcol-sm-12 vcol-xs-12 paddingLeft">
            <div :class="activeTab === 'preview' ? 'prevMobile' : 'prevDesktop'" class="model-sections height100">
              <div class="previewHeader">
                <p class="vtext-card-title">Preview</p>
              </div>
              <div class="jmpr_whatsapp_roundButton">
                <img class="preview" src="@/resources/images/whatsa_widget_icon.jpeg" alt="WhatsApp widget preview" />
                <span class="jmpr_whatsapp_greeting_title_tooltip" :style="{'background-color': welcomeMessage.theme_color, 'color': welcomeMessage.text_color}">{{ welcomeMessage.welcome_screen_greeting }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </master-layout>
</template>

<script>
import HelpTooltip from '@/components/HelpTooltip.vue';
import { useUIStore } from '@/stores/ui';
import { isLive, isEuServer } from '@/helpers/env.helpers';
import { useSocialChannels } from '@/composables/useSocialChannels';
import { countryCodeWithout_plusSign } from '@/helpers/sms.helper';

export default {
  components: { HelpTooltip },
  setup() {
    const { whatsAppSocialData } = useSocialChannels();
    return { whatsAppSocialData };
  },
  data() {
    return {
      activeTab: 'widget',
      welcomeMessage: {
        theme_color: '#075e54ff',
        text_color: '#ffffffff',
        intent_message: 'Hi there...',
        welcome_screen_greeting: 'Question? We can help.'
      }
    }
  },
  computed: {
    baseURL() {
      if (isLive()) {
        if (isEuServer()) return "https://dashboard.jumper-emea.com";
        return "https://dashboard.jumper.ai";
      }
      return "https://dashboard.jumperdev.com";
    }
  },
  methods: {
    countryCodeWithout_plusSign,
    isEuServer,
    copy() {
      try {
        const codeBlock = document.getElementById("codeSnippet");
        if (codeBlock) {
          const textToCopy = codeBlock.innerText.replace(/\n\s*\n/g, "\n");
          navigator.clipboard.writeText(textToCopy).then(() => {
            useUIStore().updateToast({
              open: true,
              message: "Copied to the clipboard!",
              type: "success"
            });
          }).catch(err => {
            console.error("Copy failed: ", err);
            useUIStore().updateToast({
              open: true,
              message: "Something went wrong",
              type: "danger"
            });
          });
        }
      } catch (error) {
        console.error("Exception: ", error);
        useUIStore().updateToast({
          open: true,
          message: "Something went wrong",
          type: "danger"
        });
      }
    }
  }
}
</script>

<style scoped>
.toolbar-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 15px 20px;
  background: var(--white);
  border-bottom: 1px solid var(--background-primary);
}
.backBttn {
  cursor: pointer;
}

/* Mobile tabs */
.mobile-tabs {
  display: none;
  background: var(--white);
  padding: 10px 20px;
  gap: 10px;
  border-bottom: 1px solid var(--background-primary);
}
.tab-item {
  background: none;
  border: none;
  font: inherit;
  font-size: 14px;
  font-weight: 400;
  color: var(--gray-04);
  padding: 8px 16px;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.2s;
}
.tab-item.active {
  color: var(--purple);
  border-bottom-color: var(--purple);
  font-weight: 500;
}

.container {
  margin: 20px;
}
.model-sections {
  background: var(--white);
  border-radius: 5px;
}
.customMarginBottom {
  margin-bottom: 20px;
}
.sectionPadding {
  padding: 20px;
}
.custBorder {
  border-bottom: 1px solid var(--background-primary);
}
.options-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 20px;
  margin-bottom: 20px;
}
.flexContainer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}
.paddingRight {
  padding-right: 10px;
}
.colorPickerWrapper {
  min-height: 45px;
  min-width: 45px;
  border-radius: 50%;
  display: inline-block;
  border: 1px solid var(--background-primary);
  cursor: pointer;
  position: relative;
  overflow: hidden;
}
.themePicker {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  border: none;
  padding: 0;
}
.vivid-input {
  background: var(--background-primary);
  border-radius: 5px;
  min-height: 40px;
  width: 100%;
}
.vivid-textarea {
  background: var(--background-primary);
  border-radius: 5px;
  width: 100%;
}
.intentContainer {
  display: flex;
  align-items: center;
  gap: 5px;
}
.sourceCodeHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.code-block {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
}
.code-block pre {
  background-color: var(--background-primary);
  color: var(--black);
  padding: 20px;
  margin: 0;
  border-radius: 8px;
  font-family: 'SF Mono', 'Fira Code', 'JetBrains Mono', Consolas, monospace;
  font-size: 13px;
  line-height: 1.6;
  overflow-x: auto;
  white-space: pre;
  word-break: normal;
  word-wrap: normal;
}
.code-block code {
  font-family: inherit;
  font-size: inherit;
  color: inherit;
}
.previewHeader {
  border-bottom: 1px solid var(--background-primary);
  padding: 20px;
}
.paddingLeft {
  padding-left: 20px;
}
.height100 {
  min-height: 72vh;
}
.preview {
  margin: 10px 30px 30px 30px;
  border-radius: 50%;
  width: 50px;
  height: 50px;
}
.jmpr_whatsapp_roundButton {
  position: relative;
  display: inline-block;
}
.jmpr_whatsapp_roundButton .jmpr_whatsapp_greeting_title_tooltip {
  visibility: hidden;
  width: 80px;
  font-size: 13px;
  pointer-events: none;
  opacity: 0;
  transition: all 0.2s ease-out;
  text-align: center;
  padding: 5px;
  line-height: 1.5;
  border-radius: 5px;
  position: absolute;
  z-index: 1;
  bottom: 100%;
  left: 50%;
  margin-left: -40px;
}
.jmpr_whatsapp_roundButton:hover .jmpr_whatsapp_greeting_title_tooltip {
  visibility: visible;
  opacity: 1;
}
.jmpr_whatsapp_roundButton .jmpr_whatsapp_greeting_title_tooltip::after {
  content: " ";
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: var(--black) transparent transparent transparent;
}
.prevMobile {
  display: none;
}
.prevDesktop {
  display: block;
}

@media only screen and (max-width: 760px) {
  .mobile-tabs {
    display: flex;
  }
  .prevMobile {
    display: block;
  }
  .prevDesktop {
    display: none;
  }
  .height100 {
    min-height: 100dvh;
  }
  .paddingLeft {
    padding-left: 0px;
  }
}
</style>
